import { AccessToken, TokenCredential } from '@azure/core-auth';
import { useAccount, useIsAuthenticated, useMsal } from '@azure/msal-react';
import { useCallback, useState } from 'react';
import { isMsalEnabled } from '~/lib/msal';
import { handleError } from '~/utils/errors';
import { checkIsInSessionRouteWithoutRouter } from '../session/useIsInSessionRoute';

type MSALTokenOptions = {
    scopes: string[];
};

export interface TokenProvider extends TokenCredential {
    getToken: () => Promise<AccessToken | null>;
}

/**
 * Hook that provides a token provider for active MSAL account.
 *
 * @param {MSALTokenOptions} options - Options for the retrieved access token.
 *   `scopes` - An array of strings representing the scopes for which the token is requested.
 * @returns An object containing the token provider and authentication status.
 *   `tokenProvider` - The token provider for Azure services.
 *   `isAuthenticated` - Whether the user is authenticated.
 */
export const useMsalTokenProvider = ({ scopes }: MSALTokenOptions) => {
    const isInConnectorRoute = checkIsInSessionRouteWithoutRouter();
    const isAuthenticated = useIsAuthenticated() || isInConnectorRoute;
    const { instance } = useMsal();
    const account = useAccount();

    const acquireTokenSilent = useCallback(async (): Promise<AccessToken> => {
        // Skip token acquisition if MSAL is disabled or in connector route
        if (isInConnectorRoute || !isMsalEnabled()) {
            return { token: '', expiresOnTimestamp: 0 };
        }

        try {
            if (!account) {
                throw new Error(
                    'No active account set. User may need to sign in.'
                );
            }

            const tokenResult = await instance.acquireTokenSilent({
                scopes,
                account,
            });

            return {
                token: tokenResult.accessToken,
                expiresOnTimestamp: tokenResult.expiresOn?.getTime() || 0,
            };
        } catch (error) {
            handleError(
                error,
                'Silent token acquisition failed. User may need to sign in again.'
            );
            return { token: '', expiresOnTimestamp: 0 };
        }
    }, [instance, scopes, isInConnectorRoute, account]);

    const [tokenProvider] = useState<TokenProvider>({
        async getToken(): Promise<AccessToken | null> {
            return acquireTokenSilent();
        },
    });

    return { tokenProvider, isAuthenticated };
};
